import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Mail, MapPin, Clock, Send, Loader2, CheckCircle, XCircle, MessageSquare, Phone } from 'lucide-react';
import { Button } from './ui/button';
import PhoneInput from './PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { currentLanguage, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [emailError, setEmailError] = useState('');
  const [status, setStatus] = useState({ type: null, message: '' });

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const content = {
    en: {
      badge: "Get in Touch",
      title: "Contact Us",
      subtitle: "Have questions? We're here to help with your research needs",
      form: {
        name: "Your Name",
        email: "Email Address",
        phone: "Mobile Number",
        phonePlaceholder: "+34 600 000 000",
        subject: "Subject",
        message: "Your Message",
        send: "Send Message",
        sending: "Sending...",
        emailError: "Please enter a valid email address"
      },
      info: {
        title: "Contact Information",
        description: "Reach out to our team for research inquiries, product questions, or partnership opportunities.",
        email: "info@penlabpeptide.com",
        address: "Madrid, Spain",
        hours: "Mon-Fri: 9:00 - 18:00"
      },
      disclaimer: "For research inquiries only. Please note that all our products are for research purposes only and not for human consumption.",
      success: "Your message has been sent successfully! We'll get back to you soon.",
      error: "Failed to send message. Please try again or email us directly.",
      sendAnother: "Send another message"
    },
    es: {
      badge: "Contáctanos",
      title: "Contáctenos",
      subtitle: "¿Tiene preguntas? Estamos aquí para ayudarle con sus necesidades de investigación",
      form: {
        name: "Su Nombre",
        email: "Correo Electrónico",
        phone: "Número de Móvil",
        phonePlaceholder: "+34 600 000 000",
        subject: "Asunto",
        message: "Su Mensaje",
        send: "Enviar Mensaje",
        sending: "Enviando...",
        emailError: "Por favor, introduzca un correo electrónico válido"
      },
      info: {
        title: "Información de Contacto",
        description: "Comuníquese con nuestro equipo para consultas de investigación, preguntas sobre productos u oportunidades de asociación.",
        email: "info@penlabpeptide.com",
        address: "Madrid, España",
        hours: "Lun-Vie: 9:00 - 18:00"
      },
      disclaimer: "Solo para consultas de investigación. Tenga en cuenta que todos nuestros productos son solo para fines de investigación y no para consumo humano.",
      success: "¡Su mensaje ha sido enviado con éxito! Nos pondremos en contacto pronto.",
      error: "Error al enviar el mensaje. Por favor, inténtelo de nuevo o envíenos un correo directamente.",
      sendAnother: "Enviar otro mensaje"
    },
    ru: {
      badge: "Связаться с нами",
      title: "Контакты",
      subtitle: "Есть вопросы? Мы готовы помочь вам в ваших исследованиях",
      form: {
        name: "Ваше имя",
        email: "Электронная почта",
        phone: "Номер телефона",
        phonePlaceholder: "+7 900 000 0000",
        subject: "Тема",
        message: "Ваше сообщение",
        send: "Отправить сообщение",
        sending: "Отправка...",
        emailError: "Пожалуйста, введите корректный адрес электронной почты"
      },
      info: {
        title: "Контактная информация",
        description: "Свяжитесь с нашей командой по вопросам исследований, информации о продуктах или сотрудничества.",
        email: "info@penlabpeptide.com",
        address: "Мадрид, Испания",
        hours: "Пн-Пт: 9:00-18:00"
      },
      disclaimer: "Только для научных запросов. Обратите внимание: вся наша продукция предназначена исключительно для исследовательских целей и не подлежит употреблению человеком.",
      success: "Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.",
      error: "Не удалось отправить сообщение. Пожалуйста, попробуйте ещё раз или напишите нам напрямую.",
      sendAnother: "Отправить ещё одно сообщение"
    },
    de: {
      badge: "Kontakt aufnehmen",
      title: "Kontakt",
      subtitle: "Haben Sie Fragen? Wir helfen Ihnen gerne bei Ihren Forschungsanforderungen",
      form: {
        name: "Ihr Name",
        email: "E-Mail-Adresse",
        phone: "Mobilnummer",
        phonePlaceholder: "+49 170 0000000",
        subject: "Betreff",
        message: "Ihre Nachricht",
        send: "Nachricht senden",
        sending: "Wird gesendet...",
        emailError: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
      },
      info: {
        title: "Kontaktinformationen",
        description: "Wenden Sie sich an unser Team bei Forschungsanfragen, Produktfragen oder Partnerschaftsmöglichkeiten.",
        email: "info@penlabpeptide.com",
        address: "Madrid, Spanien",
        hours: "Mo-Fr: 9:00-18:00"
      },
      disclaimer: "Nur für Forschungsanfragen. Bitte beachten Sie, dass alle unsere Produkte ausschließlich Forschungszwecken dienen und nicht für den menschlichen Verzehr bestimmt sind.",
      success: "Ihre Nachricht wurde erfolgreich gesendet! Wir melden uns in Kürze bei Ihnen.",
      error: "Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.",
      sendAnother: "Weitere Nachricht senden"
    },
    fr: {
      badge: "Nous contacter",
      title: "Contactez-nous",
      subtitle: "Des questions ? Nous sommes là pour vous aider dans vos recherches",
      form: {
        name: "Votre nom",
        email: "Adresse e-mail",
        phone: "Numéro de mobile",
        phonePlaceholder: "+33 6 00 00 00 00",
        subject: "Sujet",
        message: "Votre message",
        send: "Envoyer le message",
        sending: "Envoi...",
        emailError: "Veuillez entrer une adresse e-mail valide"
      },
      info: {
        title: "Informations de contact",
        description: "Contactez notre équipe pour toute demande de recherche, question produit ou opportunité de partenariat.",
        email: "info@penlabpeptide.com",
        address: "Madrid, Espagne",
        hours: "Lun-Ven : 9h00-18h00"
      },
      disclaimer: "Uniquement pour les demandes de recherche. Tous nos produits sont destinés exclusivement à la recherche et ne sont pas destinés à la consommation humaine.",
      success: "Votre message a bien été envoyé ! Nous reviendrons vers vous très bientôt.",
      error: "Échec de l'envoi du message. Veuillez réessayer ou nous écrire directement.",
      sendAnother: "Envoyer un autre message"
    },
    it: {
      badge: "Contattaci",
      title: "Contattaci",
      subtitle: "Hai domande? Siamo qui per aiutarti con le tue esigenze di ricerca",
      form: {
        name: "Il tuo nome",
        email: "Indirizzo e-mail",
        phone: "Numero di cellulare",
        phonePlaceholder: "+39 333 0000000",
        subject: "Oggetto",
        message: "Il tuo messaggio",
        send: "Invia messaggio",
        sending: "Invio in corso...",
        emailError: "Inserisci un indirizzo e-mail valido"
      },
      info: {
        title: "Informazioni di contatto",
        description: "Contatta il nostro team per richieste di ricerca, domande sui prodotti o opportunità di partnership.",
        email: "info@penlabpeptide.com",
        address: "Madrid, Spagna",
        hours: "Lun-Ven: 9:00-18:00"
      },
      disclaimer: "Solo per richieste di ricerca. Tutti i nostri prodotti sono destinati esclusivamente a scopi di ricerca e non al consumo umano.",
      success: "Il tuo messaggio è stato inviato con successo! Ti risponderemo a breve.",
      error: "Impossibile inviare il messaggio. Riprova o scrivici direttamente.",
      sendAnother: "Invia un altro messaggio"
    },
    pl: {
      badge: "Skontaktuj się",
      title: "Kontakt",
      subtitle: "Masz pytania? Pomożemy w Twoich potrzebach badawczych",
      form: {
        name: "Twoje imię",
        email: "Adres e-mail",
        phone: "Numer telefonu",
        phonePlaceholder: "+48 600 000 000",
        subject: "Temat",
        message: "Twoja wiadomość",
        send: "Wyślij wiadomość",
        sending: "Wysyłanie...",
        emailError: "Wprowadź poprawny adres e-mail"
      },
      info: {
        title: "Dane kontaktowe",
        description: "Skontaktuj się z naszym zespołem w sprawie zapytań badawczych, pytań o produkty lub współpracy.",
        email: "info@penlabpeptide.com",
        address: "Madryt, Hiszpania",
        hours: "Pn-Pt: 9:00-18:00"
      },
      disclaimer: "Tylko dla zapytań badawczych. Wszystkie nasze produkty są przeznaczone wyłącznie do celów badawczych i nie są przeznaczone do spożycia przez ludzi.",
      success: "Twoja wiadomość została wysłana pomyślnie! Skontaktujemy się wkrótce.",
      error: "Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz do nas bezpośrednio.",
      sendAnother: "Wyślij kolejną wiadomość"
    },
    nl: {
      badge: "Neem contact op",
      title: "Contact",
      subtitle: "Vragen? We helpen u graag met uw onderzoeksbehoeften",
      form: {
        name: "Uw naam",
        email: "E-mailadres",
        phone: "Mobiel nummer",
        phonePlaceholder: "+31 6 00000000",
        subject: "Onderwerp",
        message: "Uw bericht",
        send: "Bericht verzenden",
        sending: "Verzenden...",
        emailError: "Voer een geldig e-mailadres in"
      },
      info: {
        title: "Contactinformatie",
        description: "Neem contact op met ons team voor onderzoeksvragen, productinformatie of samenwerkingsmogelijkheden.",
        email: "info@penlabpeptide.com",
        address: "Madrid, Spanje",
        hours: "Ma-Vr: 9:00-18:00"
      },
      disclaimer: "Alleen voor onderzoeksvragen. Al onze producten zijn uitsluitend bedoeld voor onderzoeksdoeleinden en niet voor menselijke consumptie.",
      success: "Uw bericht is succesvol verzonden! We nemen spoedig contact met u op.",
      error: "Bericht verzenden mislukt. Probeer het opnieuw of e-mail ons direct.",
      sendAnother: "Stuur nog een bericht"
    },
    pt: {
      badge: "Fale conosco",
      title: "Contato",
      subtitle: "Tem dúvidas? Estamos aqui para ajudar com suas necessidades de pesquisa",
      form: {
        name: "Seu nome",
        email: "Endereço de e-mail",
        phone: "Número de celular",
        phonePlaceholder: "+55 11 90000-0000",
        subject: "Assunto",
        message: "Sua mensagem",
        send: "Enviar mensagem",
        sending: "Enviando...",
        emailError: "Por favor, insira um endereço de e-mail válido"
      },
      info: {
        title: "Informações de contato",
        description: "Entre em contato com nossa equipe para consultas de pesquisa, dúvidas sobre produtos ou oportunidades de parceria.",
        email: "info@penlabpeptide.com",
        address: "Madri, Espanha",
        hours: "Seg-Sex: 9:00-18:00"
      },
      disclaimer: "Apenas para consultas de pesquisa. Todos os nossos produtos são exclusivamente para fins de pesquisa e não para consumo humano.",
      success: "Sua mensagem foi enviada com sucesso! Entraremos em contato em breve.",
      error: "Falha ao enviar a mensagem. Tente novamente ou envie um e-mail diretamente.",
      sendAnother: "Enviar outra mensagem"
    }
  };

  const c = content[currentLanguage] || content.en;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'email') {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError(c.form.emailError);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEmail(formData.email)) {
      setEmailError(c.form.emailError);
      return;
    }
    
    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to send');
      }

      setStatus({ type: 'success', message: c.success });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setStatus({ type: 'error', message: c.error });
    }
  };

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* Hero Image Background */}
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/bf28a057-bcbd-40cd-b04d-2b662e783ae3/images/6eabd4f74f595bd24921205507e2635e203625e4057e205fa44b98cea6a61f66.png"
            alt={t('contact_background_alt')}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="contactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#contactGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#contactGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <MessageSquare className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{c.badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua">
              {c.subtitle}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Contact Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="glass-card rounded-3xl p-8">
            {status.type === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-6 shadow-lg">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <p className="text-lg text-gray-700 mb-6">{status.message}</p>
                <Button 
                  className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-full px-8"
                  onClick={() => setStatus({ type: null, message: '' })}
                >
                  {c.sendAnother}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {status.type === 'error' && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700">
                    <XCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{status.message}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{c.form.name} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="capsule-input w-full"
                    placeholder={c.form.name}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{c.form.email} *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    required
                    className={`capsule-input w-full ${emailError ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder={c.form.email}
                    data-testid="contact-email"
                  />
                  {emailError && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <XCircle className="w-4 h-4" />
                      {emailError}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{c.form.phone} *</label>
                  <PhoneInput
                    value={formData.phone}
                    onChange={handleChange}
                    name="phone"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{c.form.subject} *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="capsule-input w-full"
                    placeholder={c.form.subject}
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{c.form.message} *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="capsule-input w-full resize-none"
                    placeholder={c.form.message}
                    data-testid="contact-message"
                  ></textarea>
                </div>
                <Button 
                  type="submit"
                  disabled={status.type === 'loading' || emailError}
                  className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white py-4 rounded-full font-semibold shadow-teal-glow hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                >
                  {status.type === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {c.form.sending}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {c.form.send}
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="glass-card rounded-3xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{c.info.title}</h2>
              <p className="text-gray-600 mb-8">{c.info.description}</p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-xl flex items-center justify-center shadow-teal-glow">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href="mailto:info@penlabpeptide.com" className="font-semibold text-gray-900 hover:text-capsule-teal transition-colors">{c.info.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Address</p>
                    <p className="font-semibold text-gray-900">{c.info.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-capsule-coral to-capsule-salmon rounded-xl flex items-center justify-center shadow-coral-glow">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hours</p>
                    <p className="font-semibold text-gray-900">{c.info.hours}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gradient-to-r from-capsule-teal/10 via-capsule-aqua/5 to-capsule-teal/10 border border-capsule-teal/20 rounded-2xl p-6">
              <p className="text-sm text-gray-700 leading-relaxed">{c.disclaimer}</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
